import constants from './constants';
import { getTranslation } from './translate';

const cleanVimeoEmbedId = (vimeoId) => {
  if (!vimeoId) return null;
  return String(vimeoId)
    .replace('https://vimeo.com/', '')
    .replace('https://player.vimeo.com/video/', '')
    .trim();
};

const cleanYoutubeEmbedId = (youtubeId) => {
  if (!youtubeId) return null;
  const raw = String(youtubeId).trim();
  if (raw.includes('v=')) return raw.split('v=').pop().split('&')[0];
  if (raw.includes('/')) return raw.split('/').pop();
  return raw;
};

/**
 * Collect `videos` collection UUIDs from layout translation M2M `videos` (junction shapes).
 * Preserves order; skips unknown rows; dedupes by id (first occurrence wins).
 */
export function extractOrderedCmsVideoIdsFromLayoutVideos(videos) {
  if (!Array.isArray(videos)) return [];
  const orderedCmsVideoIds = [];
  const seenIds = new Set();

  videos.forEach((junctionOrVideoRow) => {
    let cmsVideoId = null;
    if (junctionOrVideoRow == null) return;

    if (typeof junctionOrVideoRow === 'string' || typeof junctionOrVideoRow === 'number') {
      cmsVideoId = String(junctionOrVideoRow);
    } else if (typeof junctionOrVideoRow === 'object') {
      if (junctionOrVideoRow.videos_id != null) {
        const linkedVideo = junctionOrVideoRow.videos_id;
        if (linkedVideo && typeof linkedVideo === 'object' && linkedVideo.id != null) {
          cmsVideoId = String(linkedVideo.id);
        } else if (typeof linkedVideo === 'string' || typeof linkedVideo === 'number') {
          cmsVideoId = String(linkedVideo);
        }
      } else if (junctionOrVideoRow.id != null) {
        const rowHasVideosIdKey = Object.prototype.hasOwnProperty.call(
          junctionOrVideoRow,
          'videos_id',
        );
        if (rowHasVideosIdKey && junctionOrVideoRow.videos_id == null) {
          // junction row with no linked video
        } else if (
          !rowHasVideosIdKey ||
          junctionOrVideoRow.source != null ||
          junctionOrVideoRow.status != null ||
          junctionOrVideoRow.video_id != null
        ) {
          cmsVideoId = String(junctionOrVideoRow.id);
        }
      }
    }

    if (cmsVideoId && !seenIds.has(cmsVideoId)) {
      seenIds.add(cmsVideoId);
      orderedCmsVideoIds.push(cmsVideoId);
    }
  });

  return orderedCmsVideoIds;
}

/**
 * Retailer video overrides only: translated vimeo/youtube → { id, sourceUrl, videoType } for VideoGallery fallback.
 */
export function buildRecipeVideoOverrideFallbackVideos(videoOverrides, languageCode) {
  const fallbackClips = [];
  let clipSequence = 0;

  (videoOverrides || []).forEach((videoOverride, overrideIndex) => {
    const translated = getTranslation(videoOverride, { key: 'languages_code', code: languageCode });

    (translated.vimeo || []).forEach((vimeoEntry) => {
      const embedId = cleanVimeoEmbedId(vimeoEntry?.vimeo_id);
      if (!embedId) return;
      clipSequence += 1;
      fallbackClips.push({
        id: `recipe-video-override-${overrideIndex}-vimeo-${clipSequence}`,
        sourceUrl: `${constants.VIMEO_PLAYER_URL}/${embedId}`,
        videoType: 'vimeo',
      });
    });

    (translated.youtube || []).forEach((youtubeEntry) => {
      const embedId = cleanYoutubeEmbedId(youtubeEntry?.youtube_id);
      if (!embedId) return;
      clipSequence += 1;
      fallbackClips.push({
        id: `recipe-video-override-${overrideIndex}-youtube-${clipSequence}`,
        sourceUrl: `${constants.YOUTUBE_PLAYER_URL}/${embedId}`,
        videoType: 'youtube',
      });
    });
  });

  return fallbackClips;
}
